import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  getDocs,
  serverTimestamp,
  onSnapshot,
  doc,
  setDoc,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import Nav from "./Nav";
import Sidebar from "./Sidebar";
import TablaPedidos from "./TablaPedidos";
import Modal from "react-modal";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import PreviaPedidos from "./PreviaPedidos";
import { useNavContext } from "../context/NavContext";
import VendedorModal from "./VendedorModal";
import useProductos from "../hooks/useProductos";
import useArchivos from "../hooks/useArchivos";
import { useAuth } from "../context/AuthContext";
import { getSubCollection } from "../utils/getSubCollection";

moment.locale("es");
registerLocale("es", es);

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "20px",
    maxWidth: "800px",
    width: "100%",
    height: "500px",
    maxHeight: "90vh",
    overflow: "auto",
    fontFamily: "Inter, sans-serif", // Aplica la fuente
  },
  label: {
    display: "block",
    marginBottom: "10px",
  },
  input: {
    width: "100%",
    padding: "8px",
    marginBottom: "20px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  button: {
    width: "50%", // Centra el botón de agregar
    padding: "10px",
    borderRadius: "5px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    justifyContent: "center",
    margin: "0 auto",
    fontWeight: "700",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
};

function PedidoForm(props) {
  const { orden, closeModal, cotizacion } = props;
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const [productos, setProductos] = useState([]);
  const [draftId] = useState(() => orden?.id || cotizacion?.id || uuidv4());
  // Consideramos edición solo cuando orden.id existe
  const isEdit = Boolean(orden?.id);
  const parentId = isEdit ? orden.id : draftId;
  const carpeta = cotizacion ? "cotizaciones" : "pedidos";
  const {
    datos,
    descuento,
    añadirFila,
    eliminar,
    actualizarCantidad,
    handleInputChange,
    filteredProductos,
    activeRow,
    subtotalConDesc,
    iva,
    total,
    manejarCambioDescuento,
    obtenerIVA,
    calcularSubtotal,
    setDatos,
    setFilteredProductos,
    agregarBulk,
    setDescuento,
  } = useProductos(
    orden?.productosSeleccionados || [],
    orden?.descuento || 0,
    productos
  );
  const {
    archivos,
    cargando,
    manejarArchivos,
    eliminarArchivo,
    descargarArchivo,
    obtenerIcono,
  } = useArchivos(carpeta, parentId);
  const [divisa] = useState("MXN");
  const [cliente, setCliente] = useState("");
  const [clientes, setClientes] = useState([]);
  const [usuario, setUsuario] = useState("");
  const [asunto, setAsunto] = useState("");
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [mostrarTabla, setMostrarTabla] = useState(false);
  const [mostrarPrevia, setMostrarPrevia] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [ordenes, setOrdenes] = useState([]);
  const [formDirty, setFormDirty] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [numeroOrdenDeCompra, setNumeroOrdenDeCompra] = useState("");
  const [numeroCotizacion, setNumeroCotizacion] = useState("");
  const [notas, setNotas] = useState("");
  const { proximasAVencer, proximosEventos } = useNavContext();
  const [terminos, setTerminos] = useState("");
  const [referencia, setReferencia] = useState("");
  const [fechaCotizacion, setFechaCotizacion] = useState(new Date());
  const [vendedores, setVendedores] = useState([]);
  const [vendedorNombre, setVendedorNombre] = useState("");
  const [vendedorCorreo, setVendedorCorreo] = useState("");
  const [modalVendedorIsOpen, setModalVendedorIsOpen] = useState(false);
  const [planta, setPlanta] = useState("");
  const [area, setArea] = useState("");
  const [fechaDeOrden, setFechaDeOrden] = useState(new Date());
  const [rangoFechas, setRangoFechas] = useState([null, null]);
  const [startDate, endDate] = rangoFechas;

  useEffect(() => {
    // 1) Helper para asignar cliente por ID o por nombre
    const asignarCliente = (id, nombreCliente) => {
      if (id) {
        setCliente(id);
      } else if (nombreCliente) {
        const encontrado = clientes.find(
          (c) => c.empresa.toLowerCase() === nombreCliente.toLowerCase()
        );
        setCliente(encontrado ? encontrado.id : "");
      } else {
        setCliente("");
      }
    };

    const fuente = cotizacion || orden;
    if (fuente) {
      setModoEdicion(true);

      // Cliente
      asignarCliente(fuente.cliente, fuente.nombreCliente);

      // Ya no llamamos initArchivos: el hook useArchivos("pedidos", parentId)
      // se encargará de leer la subcolección
      // -- referencias y fechas --
      setReferencia(fuente.referencia || "");
      setFechaCotizacion(
        fuente.fechaCotizacion ? new Date(fuente.fechaCotizacion) : new Date()
      );
      setFechaVencimiento(
        fuente.fechaVencimiento ? new Date(fuente.fechaVencimiento) : null
      );

      // Rango de entrega (solo en orden)
      if (orden) {
        setRangoFechas([
          orden.fechaInicioRango ? new Date(orden.fechaInicioRango) : null,
          orden.fechaFinRango ? new Date(orden.fechaFinRango) : null,
        ]);
        setNumeroOrdenDeCompra(orden.numeroOrdenDeCompra || "");
      }

      // Vendedor
      setVendedorNombre(fuente.vendedorNombre || "");
      setVendedorCorreo(fuente.vendedorCorreo || "");
      setVendedores((prev) =>
        prev.some((v) => v.nombre === fuente.vendedorNombre)
          ? prev
          : [
              ...prev,
              { nombre: fuente.vendedorNombre, correo: fuente.vendedorCorreo },
            ]
      );

      // Productos y descuento (hook useProductos)
      setDatos(
        (fuente.productosSeleccionados || []).map((p) => ({
          id: uuidv4(),
          ...p,
          numeroDeParteCliente: p.numeroDeParteCliente || "",
        }))
      );
      setDescuento(parseFloat(fuente.descuento) || 0);

      // Resto de campos
      setPlanta(fuente.planta || "");
      setUsuario(fuente.usuario || "");
      setArea(fuente.area || "");
      setAsunto(fuente.asunto || "");
      setNumeroCotizacion(fuente.numeroCotizacion || "");
      setNotas(fuente.notas || "");
      setTerminos(fuente.terminos || "");
    } else {
      // Nuevo formulario: estado inicial
      setModoEdicion(false);
      setDatos([
        {
          id: uuidv4(),
          nombre: "",
          numeroDeParteCliente: "",
          cantidad: 1,
          tarifa: 0,
          iva: 0,
          descuento: 0,
          tipoDescuento: "%",
          importe: 0,
        },
      ]);
      setDescuento(0);
      // para un nuevo pedido, no hay archivos aún,
      // el hook useArchivos simplemente no suscribirá nada si parentId es undefined
    }
  }, [
    cotizacion,
    orden,
    clientes,
    setDatos,
    setDescuento,
    // ya no incluyas initArchivos aquí
  ]);

  useEffect(() => {
    // 1) Suscribirse a vendedores en tiempo real desde la misma ruta que en CotizaciónForm
    const vendedoresCol = getSubCollection(ownerUid, "vendedores");
    const unsubscribeVendedores = onSnapshot(
      vendedoresCol,
      (snap) =>
        setVendedores(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Error al suscribirse a vendedores:", err)
    );

    // 2) Cargar clientes y productosClientes una vez
    const clientesCol = getSubCollection(ownerUid, "clientes");
    const productosCol = getSubCollection(ownerUid, "productosClientes");

    const fetchClientesYProductos = async () => {
      try {
        // Clientes
        const clientesSnap = await getDocs(clientesCol);
        const clientesList = clientesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        // Filtrar únicos por empresa
        const clientesUnicos = clientesList.filter(
          (c, i, arr) => arr.findIndex((x) => x.empresa === c.empresa) === i
        );
        setClientes(clientesUnicos);

        // Productos
        const productosSnap = await getDocs(productosCol);
        const productosList = productosSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          tipoDescuento: "cantidad",
          valorDescuento: 0,
        }));
        setProductos(productosList);
      } catch (error) {
        console.error("Error al obtener clientes o productosClientes:", error);
      }
    };

    fetchClientesYProductos();

    // Cleanup al desmontar
    return () => {
      unsubscribeVendedores();
    };
  }, [ownerUid]);

  const openModalVendedor = () => setModalVendedorIsOpen(true);
  const closeModalVendedor = () => setModalVendedorIsOpen(false);

  const handleCancel = () => {
    if (formDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          // Cerrar el modal usando la función pasada como prop
          closeModal();
          setFormDirty(false); // Opcional: resetea el estado si es necesario
          setMostrarTabla(true); // Mostrar la tabla al salir
        }
      });
    } else {
      // Si no hay cambios, simplemente cierra el modal
      closeModal();
      setMostrarTabla(true); // Mostrar la tabla al salir
    }
  };

  const cerrarModalPrevia = () => {
    setMostrarPrevia(false);
    setModalIsOpen(false);
  };

  const continuarDesdePrevia = () => {
    setMostrarTabla(true);
  };

  const guardar = async (e) => {
    e.preventDefault();

    // ---------- Helpers ----------
    const nNum = (x, def = 0) => (Number.isFinite(x) ? x : def);
    const nStr = (x, def = "") => x ?? def;

    const normalizeProducto = (p) => {
      const cantidad = nNum(p?.cantidad, 1);
      const tarifa = nNum(p?.tarifa, 0);
      return {
        id: nStr(p?.id, uuidv4()),
        nombre: nStr(p?.nombre),
        descripcion: nStr(p?.descripcion ?? p?.nombre),
        modelo: nStr(p?.modelo ?? p?.numeroDeParteCliente),
        numeroDeParteCliente: nStr(p?.numeroDeParteCliente),
        cantidad,
        tarifa,
        tipoDescuento: p?.tipoDescuento ?? "%",
        descuento: nNum(p?.descuento, 0),
        importe: Number.isFinite(p?.importe)
          ? Number(p?.importe)
          : tarifa * cantidad,
        iva: nNum(p?.iva, 0),
      };
    };

    const normalizeArchivo = (a) => ({
      id: nStr(a?.id),
      nombre: nStr(a?.nombre),
      url: nStr(a?.url),
      path: nStr(a?.path),
      size: Number.isFinite(a?.size) ? a.size : null,
      type: nStr(a?.type),
    });

    const pruneUndefinedDeep = (val) => {
      if (Array.isArray(val)) return val.map(pruneUndefinedDeep);
      if (val && typeof val === "object") {
        const out = {};
        for (const [k, v] of Object.entries(val)) {
          if (v === undefined) continue;
          out[k] = pruneUndefinedDeep(v);
        }
        return out;
      }
      return val;
    };
    // -----------------------------

    /* 1) VALIDACIÓN (usa fechaDeOrden del form) */
    if (!cliente || !numeroOrdenDeCompra || !fechaDeOrden) {
      await Swal.fire({
        icon: "error",
        title: "Campos obligatorios faltantes",
        text: "Por favor completa los campos: Cliente, Número de Orden y Fecha de la Orden.",
      });
      return;
    }

    try {
      /* 2) DUPLICADOS */
      const pedidosCol = getSubCollection(ownerUid, "pedidos");
      const snap = await getDocs(pedidosCol);
      const dup = snap.docs.find(
        (d) => d.data().numeroOrdenDeCompra === numeroOrdenDeCompra
      );
      if (dup && (!modoEdicion || dup.id !== orden?.id)) {
        await Swal.fire({
          icon: "error",
          title: "Número de orden duplicado",
          text: `El número "${numeroOrdenDeCompra}" ya existe.`,
        });
        return;
      }

      /* 3) PAYLOAD (normalizado + sin undefined) */
      const clienteSel = clientes.find((c) => c.id === cliente) || {};
      const productosLimpios = (datos || []).map(normalizeProducto);
      const archivosLimpios = (archivos || []).map(normalizeArchivo);

      const baseData = {
        ownerId: ownerUid,
        cliente,
        nombreCliente: clienteSel.empresa || "",
        numeroOrdenDeCompra: nStr(numeroOrdenDeCompra),
        numeroCotizacion: nStr(numeroCotizacion),
        fechaCotizacion:
          fechaCotizacion instanceof Date
            ? fechaCotizacion.toISOString()
            : null,
        // 👇 usar la misma fecha que edita el usuario
        fechaOrden: fechaDeOrden.toISOString(),
        fechaVencimiento:
          fechaVencimiento instanceof Date
            ? fechaVencimiento.toISOString()
            : null,
        estado: "Pendiente",
        divisa: nStr(divisa, "MXN"),
        fechaInicioRango:
          startDate instanceof Date ? startDate.toISOString() : null,
        fechaFinRango: endDate instanceof Date ? endDate.toISOString() : null,
        notas: nStr(notas),
        usuario: nStr(usuario),
        planta: nStr(planta),
        vendedorNombre: nStr(vendedorNombre),
        vendedorCorreo: nStr(vendedorCorreo),
        area: nStr(area),
        referencia: nStr(referencia),
        asunto: nStr(asunto),
        terminos: nStr(terminos),
        productosSeleccionados: productosLimpios,
        total: nNum(total, 0),
        descuento: nNum(descuento, 0),
        archivos: archivosLimpios,
        ...(modoEdicion ? {} : { createdAt: serverTimestamp() }),
      };

      const data = pruneUndefinedDeep(baseData);

      /* 4) CREACIÓN O EDICIÓN */
      if (modoEdicion && orden?.id) {
        const docRef = doc(pedidosCol, orden.id);
        await setDoc(docRef, data, { merge: true });
        await Swal.fire({
          icon: "success",
          title: "Orden actualizada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        const docRef = doc(pedidosCol, draftId);
        await setDoc(docRef, data);
        await Swal.fire({
          icon: "success",
          title: "Orden guardada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });
      }

      /* 5) LIMPIEZA Y CIERRE */
      setFormDirty(false);
      setDatos([
        {
          id: uuidv4(),
          nombre: "",
          descripcion: "",
          modelo: "",
          cantidad: 1,
          tarifa: 0,
          iva: 0,
          descuento: 0,
          tipoDescuento: "%",
          importe: 0,
        },
      ]);
      setDescuento(0);
      setCliente("");
      setNumeroOrdenDeCompra("");
      setFechaDeOrden(new Date()); // 👈 resetea la que realmente usas en el form
      setNotas("");
      setTerminos("");
      closeModal();
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Error al guardar la orden",
        text: err.message,
      });
    }
  };

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <Sidebar />

      <div className="cotizacion-form">
        <form
          onKeyDown={(e) => {
            // Solo bloquear "Enter" fuera de los elementos <textarea>
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
              e.preventDefault();
            }
          }}
        >
          <div>
            <div className="group apartado">
              <label htmlFor="cliente" className="required">
                Cliente*
              </label>
              <select
                id="cliente"
                name="cliente"
                value={cliente}
                onChange={(e) => {
                  setCliente(e.target.value);
                  setFormDirty(true);
                }}
              >
                <option value="">Seleccionar cliente</option>
                {clientes.length > 0 ? (
                  clientes
                    .slice()
                    .sort((a, b) => a.empresa.localeCompare(b.empresa)) // Ordenar alfabéticamente
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.empresa}
                      </option>
                    ))
                ) : (
                  <option value="" disabled>
                    No hay clientes disponibles
                  </option>
                )}
              </select>
            </div>

            <br />

            <div className="group fechas">
              <div className="group">
                <label htmlFor="numeroCotizacionOC">No. Cotización</label>
                <div>
                  <input
                    type="text"
                    id="numeroCotizacionOC"
                    name="numeroCotizacionOC"
                    value={numeroCotizacion}
                    onChange={(e) => {
                      setNumeroCotizacion(e.target.value);
                      setFormDirty(true); // Marcar que el formulario ha sido modificado
                    }}
                  />
                </div>
              </div>

              <div className="group">
                <label htmlFor="numeroOrdenDeCompra" className="required">
                  No. Orden de Compra#*
                </label>
                <input
                  style={{ width: "40%" }}
                  type="text"
                  id="numeroOrdenDeCompra"
                  name="numeroOrdenDeCompra"
                  value={numeroOrdenDeCompra}
                  onChange={(e) => {
                    setNumeroOrdenDeCompra(e.target.value);
                    setFormDirty(true); // Marcar que el formulario ha sido modificado
                  }}
                />
              </div>
            </div>

            <div className="group">
              <label htmlFor="referencia">Referencia Interna</label>
              <input
                type="text"
                id="referencia"
                name="referencia"
                value={referencia}
                onChange={(e) => {
                  setReferencia(e.target.value);
                  setFormDirty(true); // Marcar que el formulario ha sido modificado
                }}
              />
            </div>

            <div className="group fechas" style={{ marginTop: "30px" }}>
              <div className="group">
                <label htmlFor="fecha-cotizacion">Fecha de cotización</label>
                <DatePicker
                  selected={fechaCotizacion}
                  onChange={(date) => {
                    setFechaCotizacion(date);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="dd MMM yyyy"
                  id="fecha-cotizacion"
                  locale="es"
                  readOnly
                />
              </div>
              <div className="group">
                <label htmlFor="fechaDeOrden" className="required">
                  Fecha de la Orden*
                </label>
                <DatePicker
                  selected={fechaDeOrden}
                  onChange={(date) => {
                    setFechaDeOrden(date);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="dd MMM yyyy"
                  id="fecha-de-orden"
                  locale="es"
                />
              </div>
            </div>

            <hr
              style={{
                marginBottom: "30px",
                marginTop: "20px",
                border: "1px solid #f9f9fb",
              }}
            />

            <div className="group fechas">
              <div className="group">
                <label htmlFor="vendedor">Vendedor</label>
                <select
                  id="vendedorNombre"
                  name="vendedorNombre"
                  value={
                    vendedores.find((v) => v.nombre === vendedorNombre)?.id ||
                    ""
                  }
                  style={{ width: "42%" }}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    if (selectedValue === "agregar-nuevo") {
                      openModalVendedor(); // Solo abre el modal
                    } else {
                      const vendedorSeleccionado = vendedores.find(
                        (v) => v.id === selectedValue
                      );
                      if (vendedorSeleccionado) {
                        setVendedorNombre(vendedorSeleccionado.nombre);
                        setVendedorCorreo(vendedorSeleccionado.correo);
                      }
                    }
                  }}
                >
                  <option value="">Seleccionar vendedor</option>
                  {vendedores.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nombre}
                    </option>
                  ))}
                  <option value="agregar-nuevo">Agregar nuevo vendedor</option>
                </select>
              </div>

              <VendedorModal
                isOpen={modalVendedorIsOpen}
                onClose={closeModalVendedor}
                initialNombre={vendedorNombre}
                initialCorreo={vendedorCorreo}
                onSelect={(v) => {
                  setVendedorNombre(v.nombre);
                  setVendedorCorreo(v.correo);
                }}
              />

              <div className="group">
                <label htmlFor="rango-fechas">Tiempo de Entrega</label>
                <DatePicker
                  selected={startDate}
                  onChange={(dates) => setRangoFechas(dates)}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange
                  locale="es"
                  id="rango-fechas"
                  dateFormat="dd MMM yyyy" // Formato simplificado
                  placeholderText="Selecciona un rango de fechas"
                  className="custom-date-range-input"
                  minDate={new Date()}
                  shouldCloseOnSelect={false}
                />
              </div>
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="area">Área</label>
                <input
                  type="text"
                  id="area"
                  name="area"
                  value={area}
                  onChange={(e) => {
                    setArea(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                />
              </div>

              <div className="group">
                <label htmlFor="planta">Planta</label>
                <input
                  type="text"
                  id="planta"
                  name="planta"
                  value={planta}
                  onChange={(e) => {
                    setPlanta(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                />
              </div>
            </div>

            <br />

            <div className="group fechas">
              <div className="group" style={{ marginTop: "-15px" }}>
                <label htmlFor="asunto">Asunto</label>
                <input
                  type="text"
                  id="asunto"
                  name="asunto"
                  value={asunto}
                  onChange={(e) => {
                    setAsunto(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                />
              </div>

              <div className="group" style={{ marginTop: "-15px" }}>
                <label htmlFor="usuario">Usuario</label>
                <input
                  type="text"
                  id="usuario"
                  name="usuario"
                  value={usuario}
                  onChange={(e) => {
                    setUsuario(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                />
              </div>
            </div>

            <br />

            <div className="apartado2">Tabla de productos</div>

            <div>
              {/* Contenedor para la tabla y los botones */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                {/* Tabla general */}

                <div>
                  <table className="productos-table">
                    <thead>
                      <tr>
                        <th>Detalles del Artículo</th>
                        <th style={{ textAlign: "center" }}>Modelo</th>
                        <th id="cantidad-col">Cantidad</th>
                        <th id="precio-col">Precio</th>
                        <th id="descuento-col">Descuento</th>
                        <th id="importe-col">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.map((producto) => (
                        <tr key={producto.id} style={{ position: "relative" }}>
                          <td>
                            <div style={{ position: "relative" }}>
                              <textarea
                                className="list-products"
                                value={
                                  producto.descripcion || producto.nombre || ""
                                }
                                onChange={(e) => {
                                  handleInputChange(e, producto.id);
                                  setFormDirty(true);
                                }}
                                onBlur={() =>
                                  setTimeout(
                                    () => setFilteredProductos([]),
                                    100
                                  )
                                }
                                placeholder="Escriba para seleccionar un artículo"
                                style={{
                                  width: "600px",
                                  height: "130px",
                                  padding: "8px",
                                  fontSize: "14px",
                                  lineHeight: "1.5",
                                  resize: "vertical",
                                  overflow: "auto",
                                  whiteSpace: "pre-wrap",
                                  fontFamily: "Arial, sans-serif",
                                }}
                              />
                              {activeRow === producto.id &&
                                filteredProductos.length > 0 && (
                                  <ul
                                    style={{
                                      position: "absolute",
                                      top: "105%",
                                      left: 0,
                                      width: "100%",
                                      backgroundColor: "#fff",
                                      border: "1px solid #cecece",
                                      borderRadius: "5px",
                                      zIndex: 10,
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                      listStyle: "none",
                                      padding: "5px",
                                      margin: 0,
                                    }}
                                  >
                                    {filteredProductos.map((prod) => (
                                      <li
                                        key={prod.id}
                                        onMouseDown={() => {
                                          handleInputChange(
                                            { target: { value: prod.nombre } },
                                            producto.id
                                          );
                                          setFormDirty(true);
                                        }}
                                        style={{
                                          padding: "5px",
                                          cursor: "pointer",
                                          backgroundColor: "#f9f9f9",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        {prod.nombre}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                            </div>
                          </td>

                          <td>
                            <input
                              type="text"
                              value={
                                producto.modelo ||
                                producto.numeroDeParteCliente ||
                                ""
                              }
                              onChange={(e) => {
                                const modelo = e.target.value;
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? { ...item, modelo }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                            />
                          </td>

                          <td id="cantidad-col">
                            <input
                              type="number"
                              value={producto.cantidad || 1}
                              onChange={(e) => {
                                actualizarCantidad(
                                  producto.id,
                                  parseInt(e.target.value) || 0
                                );
                                setFormDirty(true);
                              }}
                            />
                          </td>

                          <td id="precio-col">
                            <input
                              type="text"
                              value={
                                producto.tarifa !== undefined &&
                                producto.tarifa !== ""
                                  ? producto.tarifa.toString()
                                  : ""
                              }
                              onFocus={(e) => {
                                e.target.value =
                                  parseFloat(producto.tarifa) || "";
                              }}
                              onChange={(e) => {
                                const raw = e.target.value.replace(
                                  /[^0-9.]/g,
                                  ""
                                );
                                if (/^\d*\.?\d*$/.test(raw)) {
                                  const tarifa = parseFloat(raw) || 0;
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? {
                                            ...item,
                                            tarifa,
                                            importe: tarifa * item.cantidad,
                                          }
                                        : item
                                    )
                                  );
                                  setFormDirty(true);
                                }
                              }}
                              onBlur={(e) => {
                                const num = parseFloat(e.target.value);
                                if (!isNaN(num)) {
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? {
                                            ...item,
                                            tarifa: num,
                                            importe: num * item.cantidad,
                                          }
                                        : item
                                    )
                                  );
                                  e.target.value = num.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  });
                                }
                              }}
                              style={{
                                width: "100%",
                                textAlign: "right",
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "4px",
                              }}
                            />
                          </td>

                          <td id="descuento-col">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                height: "32px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                overflow: "hidden",
                                width: "100%",
                              }}
                            >
                              {/* Input de descuento */}
                              <input
                                type="text"
                                min="0"
                                step="1"
                                value={producto.descuento || 0}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (/^\d*\.?\d*$/.test(v)) {
                                    let d = parseFloat(v) || 0;
                                    if (
                                      producto.tipoDescuento === "%" &&
                                      d > 100
                                    )
                                      d = 100;
                                    setDatos((prev) =>
                                      prev.map((item) =>
                                        item.id === producto.id
                                          ? {
                                              ...item,
                                              descuento: d,
                                              importe:
                                                item.tipoDescuento === "%"
                                                  ? item.tarifa *
                                                    item.cantidad *
                                                    (1 - d / 100)
                                                  : item.tarifa *
                                                      item.cantidad -
                                                    d,
                                            }
                                          : item
                                      )
                                    );
                                    setFormDirty(true);
                                  }
                                }}
                                style={{
                                  flex: 1,
                                  border: "none",
                                  outline: "none",
                                  textAlign: "right",
                                  padding: "0 12px",
                                  fontSize: "16px",
                                  lineHeight: "32px",
                                }}
                              />

                              {/* Select de tipo con icono */}
                              <div
                                style={{
                                  position: "relative",
                                  width: "50px",
                                  height: "100%",
                                }}
                              >
                                <select
                                  value={producto.tipoDescuento || "%"}
                                  onChange={(e) => {
                                    const tipo = e.target.value;
                                    setDatos((prev) =>
                                      prev.map((item) =>
                                        item.id === producto.id
                                          ? {
                                              ...item,
                                              tipoDescuento: tipo,
                                              importe:
                                                tipo === "%"
                                                  ? item.tarifa *
                                                    item.cantidad *
                                                    (1 - item.descuento / 100)
                                                  : item.tarifa *
                                                      item.cantidad -
                                                    item.descuento,
                                            }
                                          : item
                                      )
                                    );
                                    setFormDirty(true);
                                  }}
                                  style={{
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    MozAppearance: "none",
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                    outline: "none",
                                    padding: "0 12px",
                                    fontSize: "16px",
                                    lineHeight: "32px",
                                    backgroundColor: "#f4f4f4",
                                    textAlign: "center",
                                    cursor: "pointer",
                                  }}
                                >
                                  <option value="%">%</option>
                                  <option value="$">$</option>
                                </select>
                                <i
                                  style={{
                                    position: "absolute",
                                    right: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                    color: "#666",
                                    fontSize: "12px",
                                  }}
                                />
                              </div>
                            </div>
                          </td>

                          <td
                            id="importe-col"
                            style={{ textAlign: "right", paddingRight: "10px" }}
                          >
                            $
                            {parseFloat(producto.importe || 0).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </td>

                          {/* Botones de acción */}
                          <div
                            style={{
                              position: "absolute",
                              right: "-75px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              display: "flex",
                            }}
                          >
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-three-dots-vertical"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </button>
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                color: "#dc3545",
                                cursor: "pointer",
                              }}
                              onClick={() => eliminar(producto.id)}
                            >
                              <i
                                className="bi bi-x-lg"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </button>
                          </div>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="acciones-tabla">
              <button type="button" onClick={añadirFila}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Añadir nueva fila
              </button>
              <button type="button" onClick={() => agregarBulk(datos)}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Agregar artículos a granel
              </button>
            </div>

            <div className="data-container">
              <div className="notes-container">
                <label htmlFor="customer-notes">Notas del Cliente</label>
                <textarea
                  id="customer-notes"
                  placeholder="Escribe tus notas aquí..."
                  value={notas}
                  onChange={(e) => {
                    setNotas(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                ></textarea>
              </div>

              <div className="totales-container">
                {/* Subtotal después de aplicar el descuento */}
                <div className="row">
                  <span className="label">Subtotal</span>
                  <span>
                    $
                    {subtotalConDesc.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                {/* Descuento en porcentaje */}
                <div className="row descuento">
                  <span className="label">Descuento</span>
                  <div className="descuento-input-container">
                    <input
                      type="text"
                      className="descuento-input"
                      value={descuento}
                      onChange={manejarCambioDescuento}
                    />
                    <span className="percentage-sign">%</span>
                  </div>
                  <span className="discount-percentage">
                    – $
                    {(calcularSubtotal() * (descuento / 100)).toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </span>
                </div>

                {/* IVA calculado */}
                <div className="row iva">
                  <span className="label">
                    IVA ({(obtenerIVA() * 100).toFixed(0)}%)
                  </span>
                  <span>
                    $
                    {iva.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <hr />

                {/* Total final */}
                <div className="row total">
                  <span>Total ({divisa})</span>
                  <span className="total-amount">
                    $
                    {total.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#f9f9fb",
                padding: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "10px",
                width: "94%",
                marginTop: "30px",
                paddingBottom: "30px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Left Section: Términos y Condiciones */}
              <div className="terminos-container">
                <h4>Términos y condiciones</h4>
                <textarea
                  value={terminos}
                  onChange={(e) => {
                    setTerminos(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                />
              </div>

              {/* Right Section: File Upload */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "8px",
                  flex: "0 0 530px",
                  marginTop: "30px",
                }}
              >
                <label style={{ fontSize: "14px" }}>
                  Adjuntar archivo(s) a Estimación
                </label>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px dashed #ccc",
                    borderRadius: "5px",
                    overflow: "hidden", // Eliminar cualquier contenido que se salga del borde
                  }}
                >
                  <label
                    style={{
                      padding: "8px 10px",
                      backgroundColor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      whiteSpace: "nowrap", // Evitar que el contenido del botón se ajuste en varias líneas
                    }}
                  >
                    <i style={{ color: "#000" }} className="bi bi-upload"></i>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#000",
                        fontWeight: "lighter",
                      }}
                    >
                      Cargar archivo
                    </span>
                    <input
                      type="file"
                      style={{ display: "none" }} // Ocultar el input de archivo
                      accept=".csv,.xls,.xlsx,.doc,.docx,.pdf,.jpg,.jpeg,.png,.svg,.txt"
                      onChange={manejarArchivos}
                    />
                  </label>

                  <div
                    style={{
                      padding: "10px",
                      backgroundColor: "#fff",
                      borderLeft: "1px dashed #ccc", // Borde que conecta con el input
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      borderTopRightRadius: "auto", // Bordes redondeados en la esquina derecha
                      borderBottomRightRadius: "auto",
                      minWidth: "20px", // Evitar que el botón se haga demasiado pequeño
                      margin: "0", // Eliminar cualquier margen que agregue espacio extra
                    }}
                  >
                    <i
                      style={{
                        color: "#000",
                        fontSize: "14px", // Ajustar el tamaño del ícono
                      }}
                      className="bi bi-chevron-down"
                    ></i>
                  </div>
                </div>

                <p style={{ fontSize: "12px", color: "#888" }}>
                  Puedes cargar un máximo de 10 archivos, cada uno de 10 MB
                </p>

                {cargando && (
                  <p style={{ fontSize: "14px", color: "#888" }}>
                    Cargando archivo...
                  </p>
                )}

                {archivos.length > 0 && (
                  <div style={{ width: "100%" }}>
                    <strong>Archivos cargados:</strong>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        marginTop: "10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {archivos.map(({ nombre, url }) => {
                        const ext = nombre.split(".").pop().toLowerCase();
                        const { icon, color } = obtenerIcono(ext);

                        return (
                          <li
                            key={nombre}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "10px",
                              border: "1px solid #ddd",
                              borderRadius: "5px",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                              backgroundColor: "#f9f9fb",
                            }}
                          >
                            <i
                              className={`bi ${icon}`}
                              style={{
                                color,
                                fontSize: "1.5rem",
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ flexGrow: 1 }}>{nombre}</span>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                descargarArchivo(url, nombre);
                              }}
                              style={{
                                color: "#007bff",
                                background: "none",
                                border: "none",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-download"
                                style={{ fontSize: "20px" }}
                              />
                            </button>
                            <button
                              onClick={() => eliminarArchivo(nombre)}
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#dc3545",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-trash"
                                style={{ fontSize: "20px" }}
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Grupo de botones: Guardar como borrador, Guardar y enviar, Cancelar */}
          <div className="cotizacion-toolbar">
            <div className="btn-group">
              <button
                id="guardar-enviar"
                className="btn btn-primary"
                type="submit"
                onClick={guardar}
              >
                {modoEdicion ? "Guardar" : "Guardar y enviar"}
              </button>
              <button
                id="cancelar"
                className="btn btn-secondary"
                type="button"
                onClick={handleCancel}
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>

        <Modal
          isOpen={modalIsOpen}
          onRequestClose={cerrarModalPrevia}
          contentLabel="Vista Previa"
          style={customStyles}
        >
          {mostrarPrevia && (
            <PreviaPedidos
              cliente={cliente}
              clientes={clientes}
              referencia={referencia}
              asunto={asunto}
              fechaCotizacion={fechaCotizacion}
              productosSeleccionados={datos}
              subtotalConDescuento={subtotalConDesc}
              iva={iva}
              total={total}
              descuento={descuento}
              continuarDesdePrevia={continuarDesdePrevia}
              numeroCotizacion={numeroCotizacion}
            />
          )}
          <div className="modal-buttons">
            <button onClick={cerrarModalPrevia} className="eliminarBtnModal">
              Cerrar
            </button>
          </div>
        </Modal>

        {mostrarTabla && (
          <TablaPedidos
            numeroOrdenDeCompra={numeroOrdenDeCompra}
            ordenes={ordenes}
            setOrdenes={setOrdenes}
          />
        )}
      </div>
    </>
  );
}

export default PedidoForm;
